import { Controller } from "@hotwired/stimulus"

// Wizard de demande de poste pour un membre.
// Étapes : pôle → poste → période → récap (vérification puis envoi).
export default class extends Controller {
  static targets = [
    "emptyState",
    "wizardPanel",
    "step",
    "stepDot",
    "stepLabel",
    "poleSelect",
    "posteSelect",
    "dateDebut",
    "dateFin",
    "nextBtn",
    "prevBtn",
    "submitBtn",
    "recapPole",
    "recapPoste",
    "recapDateDebut",
    "recapDateFin",
    "recapDuree",
    "error",
    "form",
  ]

  static values = { current: { type: Number, default: 0 } }

  connect() {
    this.render()
  }

  openWizard(event) {
    event?.preventDefault()
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = true
    if (this.hasWizardPanelTarget) this.wizardPanelTarget.hidden = false
    this.element.classList.add("is-open")
  }

  closeWizard(event) {
    event?.preventDefault()
    if (this.hasWizardPanelTarget) this.wizardPanelTarget.hidden = true
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false
    this.element.classList.remove("is-open")
    this.currentValue = 0
    this.render()
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.closeWizard(event)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  next(event) {
    event?.preventDefault()
    if (!this.validateStep(this.currentValue)) return
    if (this.currentValue < this.stepTargets.length - 1) {
      this.currentValue += 1
      this.render()
    }
  }

  prev(event) {
    event?.preventDefault()
    if (this.currentValue > 0) {
      this.currentValue -= 1
      this.clearError()
      this.render()
    }
  }

  // Met à jour l'UI : visibilité des panneaux, dots, boutons, et récap.
  render() {
    this.stepTargets.forEach((panel, idx) => {
      const active = idx === this.currentValue
      panel.classList.toggle("is-active", active)
      panel.setAttribute("aria-hidden", active ? "false" : "true")
    })
    this.stepDotTargets.forEach((dot, idx) => {
      dot.classList.toggle("is-active", idx === this.currentValue)
      dot.classList.toggle("is-done", idx < this.currentValue)
    })

    const isLast = this.currentValue === this.stepTargets.length - 1
    if (this.hasNextBtnTarget) this.nextBtnTarget.hidden = isLast
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = !isLast
    if (this.hasPrevBtnTarget) this.prevBtnTarget.hidden = this.currentValue === 0

    if (isLast) this.renderRecap()
    this.clearError()
  }

  validateStep(stepIndex) {
    switch (stepIndex) {
      case 0:
        if (!this.poleSelectTarget.value) {
          return this.showError("Choisissez un pôle pour continuer.")
        }
        return true
      case 1:
        if (!this.posteSelectTarget.value) {
          return this.showError("Choisissez un poste pour continuer.")
        }
        return true
      case 2: {
        const debut = this.dateDebutTarget.value
        const fin = this.dateFinTarget.value
        if (!debut) return this.showError("Renseignez la date de début.")
        if (!fin) return this.showError("Renseignez la date de fin.")
        if (new Date(fin) <= new Date(debut)) {
          return this.showError("La date de fin doit être postérieure à la date de début.")
        }
        return true
      }
      default:
        return true
    }
  }

  // Étape 4 : remplit le récap et coche la checklist de vérifications.
  renderRecap() {
    const poleOpt = this.poleSelectTarget.selectedOptions[0]
    const posteOpt = this.posteSelectTarget.selectedOptions[0]
    const debut = this.dateDebutTarget.value
    const fin = this.dateFinTarget.value

    if (this.hasRecapPoleTarget) this.recapPoleTarget.textContent = poleOpt?.textContent?.trim() || "—"
    if (this.hasRecapPosteTarget) this.recapPosteTarget.textContent = posteOpt?.textContent?.trim() || "—"
    if (this.hasRecapDateDebutTarget) this.recapDateDebutTarget.textContent = this.formatDate(debut)
    if (this.hasRecapDateFinTarget) this.recapDateFinTarget.textContent = this.formatDate(fin)

    if (this.hasRecapDureeTarget) {
      this.recapDureeTarget.textContent = this.computeDuration(debut, fin)
    }
  }

  formatDate(value) {
    if (!value) return "—"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
  }

  computeDuration(debut, fin) {
    if (!debut || !fin) return "—"
    const a = new Date(debut)
    const b = new Date(fin)
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b <= a) return "—"
    const months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
    if (months < 1) {
      const days = Math.round((b - a) / (1000 * 60 * 60 * 24))
      return `${days} jour${days > 1 ? "s" : ""}`
    }
    return `${months} mois`
  }

  showError(message) {
    if (!this.hasErrorTarget) return false
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
    return false
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }
}
